import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { AuctionModelService, AuctionReportLineItem } from '../../model/auction.model';
import { NbDialogService } from '@nebular/theme';
import { AuctionReportAdjustDialogComponent } from '../auction-report-adjust-dialog/auction-report-adjust-dialog.component'
import { AuctionReportUpdateWinningBuyerDialogComponent } from '../auction-report-update-winning-buyer-dialog/auction-report-update-winning-buyer-dialog.component';
import { AuctionReportSplitDialogComponent } from '../auction-report-split-dialog/auction-report-split-dialog.component';
import { AuctionStatus, SaleStatus } from 'src/app/API.service';

interface AuctionReportMessageEvent {
  message: 'updatePaymentStatus' | 'splitCompleted' | 'adjustmentCompleted' | 'saleStatusChanged';
  lot: AuctionReportLineItem;
}

interface AdjustDialogResult {
  success: boolean;
  lineItem?: AuctionReportLineItem;
}

interface SplitDialogResult {
  success: boolean;
}

@Component({
  selector: '[app-auction-report-lot-detail]',
  templateUrl: './auction-report-lot-detail.component.html',
  styleUrls: ['./auction-report-lot-detail.component.scss']
})
export class AuctionReportLotDetailComponent implements OnInit {

  @Input() lot: AuctionReportLineItem = new AuctionReportLineItem();
  @Input() auctionStatus: AuctionStatus = AuctionStatus.LIVE;
  @Input() saleStatus: SaleStatus = SaleStatus.UNSOLD;
  @Output() messageEvent = new EventEmitter<AuctionReportMessageEvent>();

  public selectedItem: string | undefined;

  public paymentStatusOptions = [
    { key: 'UNPAID', value: 'Unpaid' },
    { key: 'DIRECT_PAID', value: 'Direct-Paid' },
    { key: 'PAID_SALE_REP', value: 'Paid Sale Rep' },
    { key: 'UNCOLLECTIBLE', value: 'Uncollectible' },
  ];

  public soldStatusOptions = [
    { key: 'SOLD', value: 'Sold' },
    { key: 'UNSOLD', value: 'Unsold' },
  ];

  public items = [
    { title: 'View Invoice' },
    { title: 'Resend Invoice' }
  ]

  constructor(private dialogService: NbDialogService,
    private auctionModel: AuctionModelService) { }

  ngOnInit(): void {


  }

  public adjustReport(): void {
    this.dialogService.open(AuctionReportAdjustDialogComponent, {
      hasScroll: false,
      closeOnBackdropClick: true,
      closeOnEsc: true,
      context: {
        lineItem: this.lot
      },
      dialogClass: 'modal-large'
    }).onClose.subscribe((result: AdjustDialogResult | undefined) => {
      if (result && result.success) {
        // Adjustment was successful, emit event to parent to trigger recalculation
        this.messageEvent.emit({ message: 'adjustmentCompleted', lot: result.lineItem || this.lot });
      }
    });
  }

  public adjustWinningBuyerInfo(): void {
    this.dialogService.open(AuctionReportUpdateWinningBuyerDialogComponent, {
      hasScroll: false,
      closeOnBackdropClick: true,
      closeOnEsc: true,
      context: {
        lineItem: this.lot
      },
      dialogClass: 'modal-medium'
    });
  }

  public splitInvoice(): void {
    this.dialogService.open(AuctionReportSplitDialogComponent, {
      hasScroll: false,
      closeOnBackdropClick: true,
      closeOnEsc: true,
      context: {
        lineItem: this.lot
      },
      dialogClass: 'modal-large'
    }).onClose.subscribe((result: SplitDialogResult | undefined) => {
      if (result && result.success) {
        // Split was successful, emit event to parent to refresh the auction report
        this.messageEvent.emit({ message: 'splitCompleted', lot: this.lot });
      }
    });
  }

  public async onUpdateSaleStatus(): Promise<void> {
    try {
      await this.auctionModel.activeAuctionReport.updateSaleStatus(this.lot);
      // Emit event to parent to trigger recalculation of totals
      this.messageEvent.emit({ message: 'saleStatusChanged', lot: this.lot });
    } catch (e) {
      console.log('Error occurred updating sale status');
    }
  }

  public async onUpdatePaymentStatus(): Promise<void> {
    this.messageEvent.emit({ message: 'updatePaymentStatus', lot: this.lot });
  }

  public isAuctionSettled() {
    return this.auctionModel.activeAuction.status === AuctionStatus.SETTLED;
  }

  /**
   * Formats a price value to show whole numbers without decimals, but show two decimal places when decimals exist.
   * Examples: 1000 -> "$1,000", 1000.50 -> "$1,000.50", 1000.5 -> "$1,000.50"
   */
  public formatPrice(price: number | null | undefined): string {
    if (price == null || isNaN(price)) {
      return '$0';
    }

    // Check if the number has decimal places (not a whole number)
    const hasDecimals = price % 1 !== 0;

    if (hasDecimals) {
      // Show 2 decimal places when there are decimals
      return `$${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      // Show no decimals for whole numbers
      return `$${price.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
  }

}
